import { useState } from "react";
import { ShieldAlert, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

// First-time MFA enrolment card. Uses only auth-level endpoints (/auth/mfa/*), so it
// works BEFORE MFA exists — this is the way out of the MFA_SETUP_REQUIRED lockout.
export function MfaSetupCard() {
  const { user, refreshUser } = useAuth();
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [disableOpen, setDisableOpen] = useState(false);
  const [disableCode, setDisableCode] = useState("");
  const [done, setDone] = useState(false);

  const disable = async () => {
    setBusy(true); setError("");
    try {
      await api.post("/auth/mfa/disable", { code: disableCode, recovery_code: disableCode });
      setDone(true);
      // every session (including this one) was just revoked — back to a fresh login
      setTimeout(() => { window.location.href = "/login"; }, 1800);
    } catch (e) {
      setError(formatApiError(e, "That code wasn't accepted — check your authenticator and try again"));
    } finally { setBusy(false); }
  };

  const begin = async () => {
    setBusy(true); setError("");
    try { setSetup((await api.post("/auth/mfa/setup")).data); }
    catch (e) { setError(formatApiError(e, "Couldn't start MFA setup")); }
    finally { setBusy(false); }
  };

  const enable = async () => {
    setBusy(true); setError("");
    try {
      const r = await api.post("/auth/mfa/enable", { code });
      setRecovery(r.data.recovery_codes || []);
      setSetup(null); setCode("");
      await refreshUser().catch(() => {});
      window.dispatchEvent(new Event("ht:mfa-enabled"));
    } catch (e) { setError(formatApiError(e, "Couldn't enable MFA")); }
    finally { setBusy(false); }
  };

  if (user?.mfa_enabled && !recovery) {
    return (
      <div className="border border-green-200 bg-green-50 rounded-md p-4" data-testid="mfa-card-on">
        <p className="text-sm font-semibold text-green-900 flex items-center gap-2">
          <ShieldCheck className="h-4 w-4" /> Multi-factor authentication is enabled on your account.
        </p>
        {!disableOpen ? (
          <button onClick={() => { setDisableOpen(true); setError(""); }} data-testid="mfa-disable-open"
            className="mt-3 h-10 px-4 border border-zinc-300 text-zinc-700 text-sm font-semibold rounded-md hover:bg-white transition-colors duration-200">
            Turn off MFA…
          </button>
        ) : (
          <div className="mt-3 border border-zinc-200 bg-white rounded-md p-3" data-testid="mfa-disable-panel">
            <p className="text-xs text-zinc-600">
              Confirm with a current authenticator or recovery code. This turns MFA off for your account
              only, signs out every session (including this one), records it in the audit log and emails
              you a security notification.
            </p>
            <div className="flex flex-wrap gap-2 mt-2">
              <input value={disableCode} onChange={(e) => setDisableCode(e.target.value)} inputMode="numeric"
                placeholder="Authenticator or recovery code" data-testid="mfa-disable-code"
                className="h-10 px-3 border border-zinc-300 rounded-md text-sm tracking-widest w-56" />
              <button onClick={disable} disabled={busy || disableCode.trim().length < 6} data-testid="mfa-disable-confirm"
                className="h-10 px-4 bg-red-700 text-white text-sm font-semibold rounded-md hover:bg-red-800 disabled:opacity-50 transition-colors duration-200">
                {busy ? "Turning off…" : "Turn off MFA"}
              </button>
              <button onClick={() => { setDisableOpen(false); setDisableCode(""); setError(""); }} data-testid="mfa-disable-cancel"
                className="h-10 px-3 text-sm font-semibold text-zinc-500 hover:text-zinc-800">
                Cancel
              </button>
            </div>
          </div>
        )}
        {error && <p className="text-xs text-red-700 mt-2" data-testid="mfa-disable-error">{error}</p>}
        {done && (
          <p className="text-xs font-semibold text-green-800 mt-2" data-testid="mfa-disable-done">
            MFA is off and all sessions were signed out — taking you to sign in…
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="border border-amber-300 bg-amber-50 rounded-md p-5" data-testid="mfa-setup-card">
      <p className="text-sm font-bold text-amber-900 flex items-center gap-2">
        <ShieldAlert className="h-4 w-4" /> Add multi-factor authentication to your admin account
      </p>
      <p className="text-xs text-amber-800 mt-1">
        Optional but recommended. Add HiredToday to an authenticator app whenever convenient —
        your account works normally until then. Once enabled, every login asks for the 6-digit code.
      </p>
      {error && <p className="text-xs text-red-700 mt-2" data-testid="mfa-setup-error">{error}</p>}
      {!setup ? (
        <button onClick={begin} disabled={busy} data-testid="mfa-setup-begin"
          className="mt-3 h-10 px-4 bg-zinc-950 text-white text-sm font-semibold rounded-md disabled:opacity-50">
          {busy ? "Preparing…" : "Set up MFA"}
        </button>
      ) : (
        <div className="mt-3 space-y-2">
          <p className="text-xs text-zinc-700">Secret (type it into your app if you can't scan):</p>
          <p className="font-mono text-sm bg-white rounded px-3 py-2 select-all border border-amber-200" data-testid="mfa-setup-secret">{setup.secret}</p>
          <p className="text-[11px] text-zinc-500 break-all">{setup.uri}</p>
          <div className="flex gap-2">
            <input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" placeholder="6-digit code"
              data-testid="mfa-setup-code"
              className="h-10 px-3 border border-zinc-300 rounded-md text-sm tracking-widest w-40" />
            <button onClick={enable} disabled={busy || code.trim().length < 6} data-testid="mfa-setup-enable"
              className="h-10 px-4 bg-green-700 text-white text-sm font-semibold rounded-md disabled:opacity-50">
              {busy ? "Checking…" : "Enable MFA"}
            </button>
          </div>
        </div>
      )}
      {recovery && (
        <div className="mt-3 border border-amber-300 bg-white rounded-md p-3" data-testid="mfa-recovery-codes">
          <p className="text-xs font-semibold text-amber-900">MFA is on. Recovery codes — shown once, store them safely:</p>
          <div className="grid grid-cols-2 gap-1 mt-2 font-mono text-xs">
            {recovery.map((c) => <span key={c} className="bg-amber-50 rounded px-2 py-1 select-all">{c}</span>)}
          </div>
        </div>
      )}
    </div>
  );
}
